import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { api } from "@/lib/api";
import useSeo from "@/hooks/useSeo";

const BRAND = [
  { src: "/images/brand/spa.jpg", caption: "Spa at The Ananta Hotels", slug: "experiences", place: "Experiences" },
  { src: "/images/brand/wedding.jpg", caption: "Wedding venue at The Ananta Hotels", slug: "experiences", place: "Experiences" },
];

const Lightbox = ({ photos, index, setIndex }) => {
  const p = photos[index];
  const go = (d) => setIndex((index + d + photos.length) % photos.length);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") setIndex((index + 1) % photos.length);
      if (e.key === "ArrowLeft") setIndex((index - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, photos.length, setIndex]);
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="gallery-lightbox" onClick={() => setIndex(null)}>
      <div className="flex justify-between items-center p-4 text-white text-sm">
        <span data-testid="gallery-lightbox-caption">{p.caption}</span>
        <button aria-label="Close" onClick={() => setIndex(null)} data-testid="gallery-lightbox-close"><X size={26} /></button>
      </div>
      <div className="flex-1 flex items-center justify-center px-2 sm:px-16 pb-6 relative min-h-0" onClick={(e) => e.stopPropagation()}>
        <img src={p.src} alt={p.caption} className="max-h-full max-w-full object-contain" data-testid="gallery-lightbox-image" />
        <button aria-label="Previous" onClick={() => go(-1)} className="absolute left-2 sm:left-5 h-11 w-11 bg-white/10 text-white flex items-center justify-center" data-testid="gallery-lightbox-prev"><ChevronLeft /></button>
        <button aria-label="Next" onClick={() => go(1)} className="absolute right-2 sm:right-5 h-11 w-11 bg-white/10 text-white flex items-center justify-center" data-testid="gallery-lightbox-next"><ChevronRight /></button>
      </div>
      <p className="text-center text-white/60 text-xs pb-4">{index + 1} / {photos.length}</p>
    </div>
  );
};

export default function Gallery() {
  useSeo("Photo Gallery - The Ananta Udaipur & Ananta Destinations", "Genuine photos of The Ananta Udaipur and The Ananta Hotels properties across India: pools, cottages, suites, villas, dining and spa.");
  const [props, setProps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [index, setIndex] = useState(null);
  useEffect(() => {
    api.get("/properties").then(async (r) => {
      const list = r.data.filter((p) => p.images?.length);
      const full = await Promise.all(list.map((p) => api.get(`/properties/${p.slug}`).then((x) => x.data).catch(() => p)));
      setProps(full.map((p) => ({ ...p, images: [...new Set([...p.images, ...(p.rooms || []).map((rm) => rm.image).filter(Boolean)])] })));
    });
  }, []);

  const all = useMemo(() => [
    ...props.flatMap((p) => p.images.map((src, n) => ({ src, slug: p.slug, place: p.city, caption: `${p.name} - photo ${n + 1}` }))),
    ...BRAND,
  ], [props]);
  const photos = filter === "all" ? all : all.filter((p) => p.slug === filter);
  const tabs = [["all", "All"], ...props.map((p) => [p.slug, p.city]), ["experiences", "Experiences"]];

  return (
    <section className="py-14 sm:py-20">
      <div className="container-x">
        <p className="eyebrow text-[#C28B5E]">Gallery</p>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl text-[#1E3B2E] tracking-tight">Moments at Ananta</h1>
        <p className="mt-4 text-sm text-[#6b6b6b] max-w-xl">Genuine photographs of our properties. Tap any photo to view it full screen.</p>
        <div className="mt-8 -mx-5 px-5 sm:mx-0 sm:px-0 flex gap-2 overflow-x-auto lg:flex-wrap pb-2" data-testid="gallery-filters">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`gallery-filter-${k}`}
              className={`shrink-0 h-9 px-4 text-[11px] uppercase tracking-[0.15em] border transition-colors ${filter === k ? "bg-[#1E3B2E] text-white border-[#1E3B2E]" : "bg-white border-[#E2DCD0] hover:border-[#1E3B2E]"}`}>{l}</button>
          ))}
        </div>
        <div className="mt-8 columns-2 md:columns-3 lg:columns-4 gap-3 [&>*]:mb-3" data-testid="gallery-grid">
          {photos.map((p, n) => (
            <button key={p.src} onClick={() => setIndex(n)} className="group block w-full overflow-hidden break-inside-avoid relative" data-testid={`gallery-photo-${n}`}>
              <img src={p.src} alt={p.caption} loading="lazy" className="w-full h-auto img-zoom" />
              <span className="absolute bottom-0 inset-x-0 p-2 text-left text-[10px] uppercase tracking-[0.15em] text-white bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">{p.place}</span>
            </button>
          ))}
        </div>
        <div className="mt-14 text-center"><Link to="/book" className="btn-primary" data-testid="gallery-book-btn">Book Now</Link></div>
      </div>
      {index !== null && photos[index] && <Lightbox photos={photos} index={index} setIndex={setIndex} />}
    </section>
  );
}
